import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronsUpDown, Check, Building2, Globe, Truck, Briefcase, Handshake, MapPin } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import api, { authApi, threePLApi, setActiveWorkspace, getActiveWorkspace } from '../lib/api';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

// Iter263 Phase K2 — Partner status badge tone. Mirrors the palette
// used on the Partner Dashboard so the switcher and the drill-down
// share the same visual language.
const PARTNER_STATUS_TONE = {
  trial:      'bg-amber-100 text-amber-800 border-amber-300',
  active:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  subscribed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  evaluating: 'bg-slate-100 text-slate-700 border-slate-300',
  suspended:  'bg-rose-100 text-rose-800 border-rose-300',
};

function partnerStatusLabel(p) {
  const raw = (p.tier || p.status || 'evaluating').toString().toLowerCase();
  if (raw === 'trial') return 'Trial';
  if (raw === 'subscribed' || raw === 'active') return 'Active';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function WorkspaceSwitcher() {
  const { user, activeWorkspace, switchWorkspace, isCrossWorkspaceMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [workspaces, setWorkspaces] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  // Iter263 Phase P — partner_admin / partner_viewer never switch
  // workspaces: their sidebar links load partner-scoped data by
  // default. Show a static partner context indicator instead of
  // the picker so the picker never traps them in a "No active
  // workspace" spiral.
  const [partnerCtx, setPartnerCtx] = useState(null);
  // Iter263 Phase R5 — partner sites for the switcher's Intake sites group.
  const [partnerSites, setPartnerSites] = useState([]);
  useEffect(() => {
    if (user?.role !== 'partner_admin' && user?.role !== 'partner_viewer') return;
    if (!user?.partner_id) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/partners/${user.partner_id}/status`,
                                   { skipWorkspaceHeader: true });
        if (alive) setPartnerCtx(r.data);
      } catch (_e) { /* fall back to name from user */ }
      try {
        const s = await threePLApi.getSites({ status: 'active' });
        if (alive) setPartnerSites(Array.isArray(s.data) ? s.data : []);
      } catch (_e) { /* leave empty */ }
    })();
    return () => { alive = false; };
  }, [user?.role, user?.partner_id]);

  const isPartnerRole = user?.role === 'partner_admin'
    || user?.role === 'partner_viewer';

  // Iter263 Phase L hotfix (Bug 3) — detect the active partner scope
  // from the URL. When ``?partner=<id>`` is present the switcher
  // header must show the partner name + a "Partner scope" indicator
  // instead of the generic "Global (All Workspaces)" label.
  const activePartnerId = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get('partner');
    } catch (_e) { return null; }
  }, [location.search]);
  const activePartner = useMemo(
    () => partners.find(p => p.id === activePartnerId) || null,
    [partners, activePartnerId],
  );

  // Filter out system-type workspaces and group by type
  const groupedWorkspaces = useMemo(() => {
    // Filter out 'system' type workspaces (redundant for System Admin)
    const filtered = workspaces.filter(w => w.workspace_type !== 'system');
    
    return {
      // Client workspaces: explicitly 'client' type OR missing type (legacy)
      client: filtered.filter(w => w.workspace_type === 'client' || !w.workspace_type || w.workspace_type === ''),
      threepl: filtered.filter(w => w.workspace_type === '3pl'),
      internal: filtered.filter(w => w.workspace_type === 'internal'),
    };
  }, [workspaces]);

  // Iter263 Phase U5 (Switcher freshness) — refactored so the same
  // fetch runs on mount AND when the dropdown opens. Prevents stale
  // caches when server-side seed adds workspaces mid-session (e.g.
  // sysadmin adds a merchant to the partner after login).
  const fetchWorkspaces = useCallback(async () => {
    try {
      const response = await authApi.getMyWorkspaces();
      setWorkspaces(response.data);

      // For System Admin: Check if already in cross-workspace mode
      const crossWorkspaceMode = localStorage.getItem('crossWorkspaceMode') === 'true';
      if (user?.role === 'system_admin' && crossWorkspaceMode) {
        setCurrentWorkspace(null);
        setLoading(false);
        return;
      }

      // Set current workspace from active workspace or find by ID
      const activeId = getActiveWorkspace() || user?.tenant_id;
      const filteredWorkspaces = response.data.filter(w => w.workspace_type !== 'system');
      const current = filteredWorkspaces.find(w => w.id === activeId);

      if (current) {
        setCurrentWorkspace(current);
      } else if (user?.role === 'system_admin') {
        setCurrentWorkspace(null);
        switchWorkspace(null);
      } else if (filteredWorkspaces.length > 0
                    && !currentWorkspace) {
        // Only auto-select when there's no active choice yet — a
        // background refresh must not silently switch the user off
        // their current workspace.
        setCurrentWorkspace(filteredWorkspaces[0]);
        setActiveWorkspace(filteredWorkspaces[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    } finally {
      setLoading(false);
    }
  }, [user, switchWorkspace]);

  useEffect(() => {
    if (user) {
      fetchWorkspaces();
    }
  }, [user, fetchWorkspaces]);

  // Iter263 Phase K2 — sysadmin-only Partner Accounts section.
  // Fetched from /admin/partners so the switcher lists partner orgs
  // alongside client/3PL/internal workspaces without leaking them to
  // non-sysadmin users.
  useEffect(() => {
    if (user?.role !== 'system_admin') {
      setPartners([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/partners');
        if (!cancelled) setPartners(res.data?.partners || []);
      } catch (_e) {
        // Silent — the section will simply hide if the fetch fails.
        if (!cancelled) setPartners([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleWorkspaceChange = (workspace) => {
    setCurrentWorkspace(workspace);
    switchWorkspace(workspace?.id || null);
    // Reload to refresh data with new workspace context
    window.location.reload();
  };

  const [globalOn, setGlobalOn] = useState(() => localStorage.getItem('overviewGlobal') === 'true');
  useEffect(() => {
    const sync = () => setGlobalOn(localStorage.getItem('overviewGlobal') === 'true');
    window.addEventListener('overview:global', sync);
    return () => window.removeEventListener('overview:global', sync);
  }, []);
  const overviewGlobal = globalOn && location.pathname === '/home';
  const toggleGlobal = (v) => {
    localStorage.setItem('overviewGlobal', String(v));
    setGlobalOn(v);
    window.dispatchEvent(new Event('overview:global'));
    if (v && location.pathname !== '/home') navigate('/home');
  };

  const handleGlobalView = () => {
    setCurrentWorkspace(null);
    switchWorkspace(null);
    // Iter263 Phase L hotfix (Bug 3) — leaving a partner scope must
    // strip ``?partner=`` so the header snaps back to Global.
    if (activePartnerId) {
      navigate(location.pathname);
    } else {
      window.location.reload();
    }
  };

  // Get display name based on current state
  const getDisplayName = () => {
    // Iter263 Phase L hotfix (Bug 3) — active partner scope wins.
    if (activePartner) {
      return activePartner.name || activePartner.id;
    }
    if (overviewGlobal || (!currentWorkspace && user?.role === 'system_admin')) {
      return 'Global (all workspaces)';
    }
    return currentWorkspace?.name || `${user?.name}'s Workspace`;
  };

  const getDisplaySubtext = () => {
    // Iter263 Phase L hotfix (Bug 3) — surface the partner scope
    // visibly (uses the same label vocabulary as the switcher menu).
    if (activePartner) {
      return `Partner scope · ${partnerStatusLabel(activePartner)}`;
    }
    if (!currentWorkspace && user?.role === 'system_admin') {
      return 'Cross-workspace view';
    }
    if (currentWorkspace?.workspace_type === '3pl') {
      return '3PL Provider';
    }
    if (currentWorkspace?.workspace_type === 'internal') {
      return 'Internal Operations';
    }
    return currentWorkspace?.status === 'active' ? 'Active' : 'Meridian Drawback UK';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-between p-2 rounded-md">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate sidebar-text">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  const hasClientWorkspaces = groupedWorkspaces.client.length > 0;
  const has3PLWorkspaces = groupedWorkspaces.threepl.length > 0;
  const hasInternalWorkspaces = groupedWorkspaces.internal.length > 0;
  // Iter263 Phase K2 — sysadmin-only Partner Accounts section.
  const hasPartnerAccounts = user?.role === 'system_admin' && partners.length > 0;

  const openPartnerDashboard = (partner) => {
    // Uses the existing ?partner=<id> route the Prospects Cockpit
    // "View workspaces" drill-down already relies on.
    navigate(`/partner?partner=${encodeURIComponent(partner.id)}`);
  };

  // Iter263 Phase R5 — partner roles get a functional dropdown showing
  // their merchant workspaces + intake sites. Partner name + tier
  // badge remain visible above the trigger as a static label.
  if (isPartnerRole) {
    const tier = partnerCtx?.tier || 'active';
    const name = partnerCtx?.name
                    || partnerCtx?.partner?.name
                    || 'Partner';
    const tone = PARTNER_STATUS_TONE[tier?.toLowerCase()]
                    || PARTNER_STATUS_TONE.evaluating;
    const partnerId = user?.partner_id;
    const activeWorkspaceId = typeof window !== 'undefined'
      ? localStorage.getItem('activeWorkspaceId')
      : null;
    const merchantWorkspaces = workspaces.filter(
      (w) => w.partner_id === partnerId
              || (w.tenant_id || w.id) === activeWorkspaceId,
    );
    const partnerCrossActive = typeof window !== 'undefined'
      && localStorage.getItem('partnerCrossWorkspaceMode') === 'true';
    const activeIsCross = partnerCrossActive || !activeWorkspaceId;

    const enterCrossMode = () => {
      localStorage.setItem('partnerCrossWorkspaceMode', 'true');
      localStorage.removeItem('activeWorkspaceId');
      // Iter263 Phase R3-1 — notify AuthContext so downstream chips
      // (Header, TenantChip) drop their stale merchant labels.
      window.dispatchEvent(new CustomEvent('partnerModeChanged'));
      navigate('/partner');
    };
    const selectWorkspace = (wsId) => {
      localStorage.removeItem('partnerCrossWorkspaceMode');
      localStorage.setItem('activeWorkspaceId', wsId);
      window.dispatchEvent(new CustomEvent('partnerModeChanged'));
      window.dispatchEvent(new CustomEvent('workspaceChanged',
                                             { detail: { workspaceId: wsId } }));
      navigate('/home');
    };
    const selectSite = (siteId) => {
      localStorage.setItem('activeSiteId', siteId);
      navigate('/intake');
    };
    const activeLabel = overviewGlobal
      ? 'Global (all workspaces)'
      : activeIsCross
      ? 'All workspaces'
      : merchantWorkspaces.find((w) => (w.tenant_id || w.id) === activeWorkspaceId)?.name
        || 'Workspace selected';

    return (
      <div className="w-full text-left min-w-0 flex items-center gap-1"
             data-testid="partner-context-indicator">
        <div className="relative flex items-center gap-1.5 pl-2 min-w-0 max-w-[55%] border-l-2 border-primary" title={`${name} (${partnerStatusLabel({ tier })})`}>
          <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
          <div className="min-w-0 flex-1 overflow-hidden flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground truncate sidebar-text"
                title={name}
                data-testid="partner-context-name">
              {name}
            </p>
            <Badge className={cn('border text-[10px] capitalize sr-only', tone)}
                     data-testid="partner-context-tier">
              {partnerStatusLabel({ tier })}
            </Badge>
          </div>
        </div>
        <DropdownMenu onOpenChange={(open) => {
          // Iter263 Phase U5 (Switcher freshness) — refresh the
          // merchant list every time the switcher opens so
          // server-side scope additions (new Nordic Goods / Soleil
          // Maison merchant added to the partner post-login) show
          // up without requiring the user to log out and back in.
          if (open) fetchWorkspaces();
        }}>
          <DropdownMenuTrigger
            className="flex-1 text-left min-w-0"
            data-testid="partner-switcher-trigger"
          >
            <div className="flex items-center gap-2 p-2 rounded-md hover:bg-accent text-sm truncate">
              <span className="truncate flex-1"
                    data-testid="partner-active-workspace-label">
                {activeLabel}
              </span>
              <ChevronsUpDown className="h-4 w-4 opacity-60 flex-shrink-0" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel>Partner context</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={enterCrossMode}
              data-testid="partner-switcher-all-workspaces"
              className={cn(activeIsCross && 'font-semibold')}
            >
              <Globe className="h-4 w-4 mr-2" /> All workspaces
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleGlobal(!globalOn)}
                              className="flex items-center justify-between cursor-pointer" data-testid="topbar-global-toggle">
              <span className="text-sm">Global view (all workspaces)</span>
              <Switch checked={globalOn} onCheckedChange={toggleGlobal} onClick={(e) => e.stopPropagation()}
                      aria-label="Global view (all workspaces)" data-testid="topbar-global-switch" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
              Merchant workspaces
            </DropdownMenuLabel>
            <DropdownMenuGroup data-testid="partner-switcher-merchants-section">
              {merchantWorkspaces.length === 0 && (
                <DropdownMenuItem disabled>No workspaces yet</DropdownMenuItem>
              )}
              {merchantWorkspaces.map((ws) => {
                const wsId = ws.tenant_id || ws.id;
                return (
                  <DropdownMenuItem
                    key={wsId}
                    onClick={() => selectWorkspace(wsId)}
                    data-testid={`partner-switcher-workspace-${wsId}`}
                    className={cn(
                      !activeIsCross && wsId === activeWorkspaceId && 'font-semibold',
                    )}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    <span className="truncate">{ws.name || wsId}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
              Intake sites
            </DropdownMenuLabel>
            <DropdownMenuGroup data-testid="partner-switcher-sites-section">
              {partnerSites.length === 0 && (
                <DropdownMenuItem disabled>No sites yet</DropdownMenuItem>
              )}
              {partnerSites.map((site) => (
                <DropdownMenuItem
                  key={site.id}
                  onClick={() => selectSite(site.id)}
                  data-testid={`partner-switcher-site-${site.id}`}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  <span className="truncate">{site.name}</span>
                  {site.code && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      {site.code}
                    </Badge>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full text-left min-w-0" data-testid="workspace-switcher">
        <div
          className={cn(
            "relative flex items-center justify-between gap-2 p-2 rounded-md hover:bg-background transition-colors min-w-0 h-11",
            // Subtle 2px primary accent when a workspace is actively
            // selected — mirrors sidebar-link-active language without
            // competing with role tone chips or status colours.
            (currentWorkspace || isCrossWorkspaceMode || activePartner)
              && "border-l-2 border-primary pl-[calc(0.5rem-2px)]"
          )}
          data-testid={activePartner
                          ? `workspace-switcher-active-partner-${activePartner.id}`
                          : 'workspace-switcher-scope'}
        >
          <div className="min-w-0 flex-1 overflow-hidden">
            <p
              className="text-sm font-semibold text-foreground truncate sidebar-text"
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={getDisplayName()}
            >
              {getDisplayName()}
            </p>
            <p
              className="text-xs text-muted-foreground truncate sidebar-text"
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {getDisplaySubtext()}
            </p>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[400px] overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Dashboard View
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(user?.role === 'system_admin' || user?.role === 'partner_admin') && (
          <>
            <DropdownMenuItem onSelect={() => toggleGlobal(!globalOn)}
                              className="flex items-center justify-between cursor-pointer" data-testid="topbar-global-toggle">
              <span className="text-sm">Global view (all workspaces)</span>
              <Switch checked={globalOn} onCheckedChange={toggleGlobal} onClick={(e) => e.stopPropagation()}
                      aria-label="Global view (all workspaces)" data-testid="topbar-global-switch" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* System Admin: Global View Option */}
        {user?.role === 'system_admin' && (
          <>
            <DropdownMenuItem
              onClick={handleGlobalView}
              className="flex items-center justify-between cursor-pointer"
              data-testid="workspace-option-global"
            >
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-blue-600">Global (All Workspaces)</p>
                  <p className="text-xs text-muted-foreground">Cross-workspace metrics</p>
                </div>
              </div>
              {!currentWorkspace && isCrossWorkspaceMode && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Client Workspaces */}
        {hasClientWorkspaces && (
          <DropdownMenuGroup data-testid="workspace-switcher-client-section">
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5 py-1">
              <Briefcase className="w-3 h-3" /> Client Workspaces
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5">
                {groupedWorkspaces.client.length}
              </Badge>
            </DropdownMenuLabel>
            {groupedWorkspaces.client.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className="flex items-center justify-between cursor-pointer pl-4"
                data-testid={`workspace-option-${workspace.id}`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{workspace.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {workspace.status === 'active' ? 'Active' : workspace.status}
                    </p>
                  </div>
                </div>
                {currentWorkspace?.id === workspace.id && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}

        {/* Iter263 Phase K2 — Partner Accounts (sysadmin-only). Rendered
            between Client Workspaces and 3PL Workspaces per spec. */}
        {hasPartnerAccounts && (
          <DropdownMenuGroup data-testid="workspace-switcher-partner-section">
            {hasClientWorkspaces && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5 py-1">
              <Handshake className="w-3 h-3" /> Partner Accounts
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5">
                {partners.length}
              </Badge>
            </DropdownMenuLabel>
            {partners.map((p) => {
              const label = partnerStatusLabel(p);
              const tone = PARTNER_STATUS_TONE[label.toLowerCase()]
                || PARTNER_STATUS_TONE.evaluating;
              return (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => openPartnerDashboard(p)}
                  className="flex items-center justify-between cursor-pointer pl-4"
                  data-testid={`workspace-partner-${p.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Handshake className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.name || p.id}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Partner org
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline"
                          className={cn('text-[10px] h-4 px-1.5 ml-2', tone)}
                          data-testid={`workspace-partner-status-${p.id}`}>
                    {label}
                  </Badge>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        )}

        {/* 3PL Workspaces */}
        {has3PLWorkspaces && (
          <DropdownMenuGroup data-testid="workspace-switcher-threepl-section">
            {(hasClientWorkspaces || hasPartnerAccounts) && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5 py-1">
              <Truck className="w-3 h-3" /> 3PL Workspaces
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5">
                {groupedWorkspaces.threepl.length}
              </Badge>
            </DropdownMenuLabel>
            {groupedWorkspaces.threepl.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className="flex items-center justify-between cursor-pointer pl-4"
                data-testid={`workspace-option-${workspace.id}`}
              >
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-warning-text" />
                  <div>
                    <p className="text-sm font-medium">{workspace.name}</p>
                    <p className="text-xs text-muted-foreground">3PL Provider</p>
                  </div>
                </div>
                {currentWorkspace?.id === workspace.id && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}

        {/* Internal Workspaces */}
        {hasInternalWorkspaces && (
          <DropdownMenuGroup data-testid="workspace-switcher-internal-section">
            {(hasClientWorkspaces || hasPartnerAccounts || has3PLWorkspaces) && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5 py-1">
              <Building2 className="w-3 h-3" /> Internal Ops
              <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1.5">
                {groupedWorkspaces.internal.length}
              </Badge>
            </DropdownMenuLabel>
            {groupedWorkspaces.internal.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className="flex items-center justify-between cursor-pointer pl-4"
                data-testid={`workspace-option-${workspace.id}`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium">{workspace.name}</p>
                    <p className="text-xs text-muted-foreground">Internal Operations</p>
                  </div>
                </div>
                {currentWorkspace?.id === workspace.id && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}

        {/* Empty State */}
        {!hasClientWorkspaces && !has3PLWorkspaces && !hasInternalWorkspaces && user?.role !== 'system_admin' && (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground text-sm">No workspaces available</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
